import classNames from "classnames";
import { type ReactElement, type HTMLAttributes, type PropsWithChildren } from "react";
import useTranslation from "../../../utils/hooks/useTranslation";
import { Tooltip } from "antd";
import TooltipCustom from "components/shared/tooltip";
import { SvgHelp, SvgInfo } from "components/svg";

export type LabelPosition = "left";

export interface FormFieldProps {
  label?: string | ReactElement;
  className?: string;
  dataLength?: string;
  dataMaxLength?: string;
  translateLabel?: boolean;
  requiredLabel?: boolean;
  translateUnit?: boolean;
  isAnt?: boolean;
  button?: ReactElement;
  icon?: any;
  customIcon?: any;
  labelPosition?: LabelPosition;
  borderLess?: boolean;
  error?: string;
  size?: string;
  info?: string;
  value?: any;
  helpText?: string;
  unit?: string;
  labelSize?: string | number;
  emptyClass?: boolean;
  placeholder?: string;
  minValue?: number;
  maxValue?: number;
  dataMaterial?: boolean;
  showInfoMessage?: boolean;
}
export const FormField: React.FC<
  PropsWithChildren<FormFieldProps & HTMLAttributes<HTMLElement>>
> = ({
  icon,
  customIcon,
  label,
  className,
  translateLabel = true,
  requiredLabel = false,
  translateUnit = true,
  button,
  children,
  dataLength,
  dataMaxLength,
  isAnt,
  labelPosition,
  borderLess = false,
  error,
  size,
  value,
  info,
  helpText,
  unit,
  labelSize,
  emptyClass = true,
  placeholder,
  minValue,
  maxValue,
  dataMaterial,
  showInfoMessage = true,
  ...rest
}) => {
    const { t } = useTranslation();
    const labelText = typeof label === "string" ? (label && translateLabel ? t(label) : label) : "";
    const unitText = unit && translateUnit ? t(unit) : unit;
    return dataMaterial ?
      <material
        data-length={dataLength ?? "auto"}
        {...rest}
        className={classNames({
          empty: emptyClass && value !== 0 && !value && !placeholder,
          info_message: info,
          validationError: error,
        })}
        data-size={size === "large" ? "large" : ""}
      >
        <div className="field">
          <label className="form_fields">
            {!!(typeof label !== "string" || labelText || helpText) && (
              <div className="data_label">
                <text data-wrap="wrap" data-label-size={labelSize}>
                  {labelText ?? label}{requiredLabel && <i data-space-horizontal="5" data-color="red">*</i>}
                </text>
                {helpText && (
                  <Tooltip
                    arrow={false}
                    color="var(--accent-dark)"
                    // space={false}
                    title={
                      <group data-direction="column" data-gap="10" data-space="5">
                        <text data-weight="700" data-wrap="wrap">
                          {helpText}
                        </text>
                      </group>
                    }
                  >
                    <icon data-help="">
                      <SvgHelp />
                    </icon>
                  </Tooltip>
                )}
              </div>
            )}
            <div className="field_cont">
              {children}
              {unitText && (
                <div className="marker" data-radius="less-special">
                  <text>{unitText}</text>
                </div>
              )}
              {button}
              <i></i>
            </div>
          </label>
        </div>
        {info && (
          <infomessage>
            <text data-wrap="wrap" className="errorMessage">
              {info}
            </text>
          </infomessage>
        )}
        {error && (
          <errormessage>
            <text data-wrap="wrap" className="errorMessage">
              {t(error, [labelText, minValue, maxValue])}
            </text>
          </errormessage>
        )}
      </material>
      :
      <div
        data-label={labelPosition}
        className={classNames("field", className, { third_part: isAnt })}
        // data-length={dataLength === "autofit" ? "autofit" : ""}
        data-length={dataLength}
        data-size={size}
        {...rest}
      >
        <label
          className={
            "form_fields " +
            classNames({
              validationError: error,
            })
          }
        >
          {!labelPosition && (labelText || typeof label !== "string") && (
            <div className="data_label">
              {labelText && <text data-wrap="wrap">{labelText}{requiredLabel && <i data-space-horizontal="5" data-color="red">*</i>}</text>}
              {typeof label !== "string" && label}
              {helpText && <TooltipCustom text={helpText}></TooltipCustom>}
            </div>
          )}

          <div className="field_cont" data-length={dataLength} data-max-length={dataMaxLength || ""}>
            {icon && (
              <>
                {typeof icon === "string" ?
                  <icon data-space-horizontal="input" data-length="input">
                    {icon}
                  </icon>
                  : icon}
                {!borderLess && (
                  <separator data-vertical="" data-height="input"></separator>
                )}
              </>
            )}
            {customIcon}
            {labelPosition === "left" && labelText && (
              <div className="data_label">
                <text data-margin="input" data-ellipsis="">
                  {labelText}{requiredLabel && <i data-space-horizontal="5" data-color="red">*</i>}
                </text>
                <separator data-vertical="" data-height="input"></separator>
              </div>
            )}

            {children}
            {button}

            {!borderLess && <i />}
          </div>
          {error && showInfoMessage ? <errormessage data-gap="10"><icon data-svg-size="14" data-svg-color="error-color"><SvgInfo /></icon>{error.indexOf('\u2002') !== -1 ? error : t(error)}</errormessage> : null}
        </label>
      </div>

  };
